import { ReactNode, createContext, useContext, PropsWithChildren, useMemo } from 'react';

import { createActorContext } from '@xstate/react';
import { ActorRefFrom, StateFrom } from 'xstate';

import { FlowRouter } from './FlowRouter';
import { Navigation as FlowNavigation, NavigationProps } from '../components';
import { basketMachine } from '../machines/basketMachine';
import { createFlowMachine } from '../machines/flowMachine';
import { BaseEvent, FlowActions, FlowContext, FlowContext as FlowMachineContext, FlowRoute } from '../types/machine';

interface Props<T extends FlowMachineContext, TEvent extends BaseEvent> {
  routes: FlowRoute<T>[];
  actions: FlowActions<T, TEvent>;
  initialContext: T;
  stickyQueryParams?: Array<string>;
  completionQueryParams?: (context: T) => Record<string, string | number | boolean | undefined>;
}

type FlowNavigationProps<T extends FlowContext> = Omit<NavigationProps<T>, 'routes'>;

type FlowMachine<T extends FlowMachineContext, TEvent extends BaseEvent> = ReturnType<
  typeof createFlowMachine<T, TEvent>
>;

type BasketMachine = typeof basketMachine;

interface HooksContextValue<T extends FlowMachineContext, TEvent extends BaseEvent = BaseEvent> {
  useFlowActorRef: () => ActorRefFrom<FlowMachine<T, TEvent>>;
  useFlowSelector: <S>(selector: (state: StateFrom<FlowMachine<T, TEvent>>) => S) => S;
  useBasketActorRef: () => ActorRefFrom<BasketMachine>;
  useBasketSelector: <S>(selector: (state: StateFrom<BasketMachine>) => S) => S;
}

interface HooksContextProviderProps<T extends FlowMachineContext, TEvent extends BaseEvent> {
  value: HooksContextValue<T, TEvent>;
  children: ReactNode;
}

const HooksContext = createContext<HooksContextValue<FlowMachineContext, BaseEvent> | null>(null);

const HooksContextProvider = <T extends FlowMachineContext, TEvent extends BaseEvent>({
  value,
  children,
}: HooksContextProviderProps<T, TEvent>) => {
  return (
    <HooksContext.Provider value={value as unknown as HooksContextValue<FlowMachineContext, BaseEvent>}>
      {children}
    </HooksContext.Provider>
  );
};

export const useFlowHooks = <T extends FlowMachineContext, TEvent extends BaseEvent = T>(): HooksContextValue<
  T,
  TEvent
> => {
  const hooks = useContext(HooksContext);
  if (!hooks) {
    throw new Error('useFlowHooks must be used within a Provider');
  }
  return hooks as unknown as HooksContextValue<T, TEvent>;
};

export const createFlowProvider = <T extends FlowMachineContext, TEvent extends BaseEvent = BaseEvent>({
  routes,
  actions,
  initialContext,
  stickyQueryParams,
  completionQueryParams,
}: Props<T, TEvent>) => {
  const flowMachine = createFlowMachine<T, TEvent>(routes, actions, initialContext);
  const FlowContext = createActorContext(flowMachine);
  const BasketContext = createActorContext(basketMachine);
  const Navigation = ({ showCategories, componentName }: FlowNavigationProps<T>) => {
    return (
      <FlowNavigation<T>
        routes={routes}
        showCategories={showCategories}
        componentName={componentName}
      />
    );
  };

  const Router = () => {
    return (
      <FlowRouter<T>
        routes={routes}
        stickyQueryParams={stickyQueryParams}
        completionQueryParams={completionQueryParams}
      />
    );
  };

  const Provider = ({ children }: PropsWithChildren) => {
    const hooks: HooksContextValue<T, TEvent> = useMemo(
      () => ({
        useFlowActorRef: FlowContext.useActorRef,
        useFlowSelector: FlowContext.useSelector,
        useBasketActorRef: BasketContext.useActorRef,
        useBasketSelector: BasketContext.useSelector,
      }),
      [],
    );

    return (
      <FlowContext.Provider
        options={{
          input: initialContext,
          systemId: 'flow-machine',
        }}>
        <BasketContext.Provider
          options={{
            systemId: 'basket-machine',
          }}>
          <HooksContextProvider value={hooks}>{children}</HooksContextProvider>
        </BasketContext.Provider>
      </FlowContext.Provider>
    );
  };

  return { Provider, Navigation, Router };
};
